import React from 'react';

import { PropertyAnchor } from 'src/util/links/property';

/**
 * Renders a property reference for a trade point.
 *
 * - Returns `null` when the trade point has no property, or the property has no
 *   resolvable label (neither title nor uuid).
 * - When `isPopover` is `true`, wraps the label in a `<strong>` tag (plain text,
 *   no navigation — suitable for popovers where an anchor would be inappropriate).
 * - Otherwise, returns a `<PropertyAnchor>` (from `src/util/links/property`)
 *   directly when a uuid is available, or falls back to a plain `<span>` with the
 *   label when uuid is absent.
 * @param {object} tradePoint
 * @param {boolean} isPopover
 * @returns {React.ReactElement|null} `<PropertyAnchor>`, `<strong>`, or `<span>` element, or null.
 */
export const buildPropertyReference = (tradePoint, isPopover) => {
  const property = tradePoint?.meter?.property;
  if (!property) {
    return null;
  }

  const { uuid, title } = property;
  const label = !title ? uuid : title;

  if (!label || label === '') {
    return null;
  }

  if (isPopover) {
    return <strong>{label}</strong>;
  }

  if (uuid) {
    return (
      <PropertyAnchor uuid={uuid}>{label}</PropertyAnchor>
    );
  }

  return <span>{label}</span>;
};

/**
 * Returns a class name when there are no trade rules.
 * Used for targeting components while launching product tours
 * @param {object} tradeRules
 * @param {string} tradeType
 * @returns {string} - class name.
 */
export const emptyTradeRulesClassName = (tradeRules, tradeType) => {
  if (tradeRules && tradeType) {
    const { buy, sell } = tradeRules;
    if (buy?.length === 0 && sell?.length === 0) {
      return ` no-trade-rules-${tradeType}`;
    }
  }
  return '';
};

/**
 * Returns a class name when there are any buy trade rule to prioritize.
 * Used for targeting components while launching product tours
 * @param {object} tradeRules
 * @param {string} tradeType
 * @returns {string} - class name.
 */
export const prioritizeTradesClassName = (tradeRules, tradeType) => {
  if (tradeRules && tradeType) {
    const { buy } = tradeRules;
    if (buy?.length > 0) {
      return ` prioritize-trade-rules-${tradeType}`;
    }
  }
  return '';
};
